// The client list (UI kit W4): who owes what, the best payer, and a table
// of every client with their invoices and money in.
import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppData } from '@/app/AppData';
import { usePageTitle } from '@/app/usePageTitle';
import { clientInitials, clientStats, contactLine, displayName, isGoodPayer } from '@/core/client';
import { today } from '@/core/dates';
import { currencyOf, Money } from '@/core/money';
import { track } from '@/data/analytics';
import { t } from '@/l10n/strings';
import { Avatar, ButtonLink, EmptyState, PageHead, PageLoading } from '@/ui/controls';
import { Icon } from '@/ui/Icon';

export function ClientsPage() {
  const { clients, invoices, business } = useAppData();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const query = params.get('q') ?? '';
  usePageTitle(t('clientsClients'));
  const day = today();

  const rows = useMemo(() => (clients ?? []).map((c) => ({ client: c, stats: clientStats(c.id, invoices ?? [], day) })), [clients, invoices, day]);
  if (!clients || !invoices || !business) return <PageLoading />;

  const q = query.trim().toLowerCase();
  const visible = rows.filter(({ client: c }) => !q || [c.name, c.company, c.email, c.phone].some((s) => s.toLowerCase().includes(q)));
  // Totals only add up clients billed in the business's own currency.
  const home = business.currency;
  const owing = rows.filter((r) => r.stats.owed.isPositive && r.stats.currency.code === home);
  const owed = owing.reduce((sum, r) => sum.plus(r.stats.owed), Money.zero(currencyOf(home)));
  const best = rows
    .filter((r) => isGoodPayer(r.stats))
    .sort((a, b) => (a.stats.averageDaysToPay ?? 99) - (b.stats.averageDaysToPay ?? 99) || b.stats.paid.minor - a.stats.paid.minor)[0];
  const month = day.slice(0, 7);
  const newThisMonth = clients.filter((c) => c.createdAt.slice(0, 7) === month).length;

  return (
    <div className="page">
      <PageHead
        title={t('clientsClients')}
        subtitle={clients.length ? t('webClientsSubtitle', { count: clients.length, owing: owing.length }) : undefined}
        actions={
          <>
            {clients.length > 0 && (
              <label className="search" style={{ width: 300 }}>
                <Icon name="search" size={18} />
                <span className="vh">{t('clientPickerSearchClients')}</span>
                <input type="search" value={query} placeholder={t('clientsSearchHint', { count: clients.length })} onChange={(e) => {
                  if (!query.trim() && e.target.value.trim()) track('search_used', { screen: 'clients' });
                  setParams(e.target.value ? { q: e.target.value } : {}, { replace: true });
                }} />
              </label>
            )}
            <ButtonLink to="/clients/new" variant="action" icon="userplus">
              {t('clientsNewClient')}
            </ButtonLink>
          </>
        }
      />
      {clients.length === 0 ? (
        <div className="card">
          <EmptyState
            badge="userplus"
            title={t('clientsNoClientsYet')}
            body={t('clientsSaveThePeopleYouBill')}
            action={
              <ButtonLink to="/clients/new" variant="action" icon="userplus">
                {t('clientsAddAClient')}
              </ButtonLink>
            }
          />
        </div>
      ) : (
        <>
          <section className="grid-3">
            <div className="kpi enter">
              <span className="caption">{t('clientsOwesYou')}</span>
              <b className="amount-lg">{owed.format()}</b>
              <span className="caption">{t('webAcrossClients', { count: owing.length })}</span>
            </div>
            {best ? (
              <Link to={`/clients/${best.client.id}`} className="hero hero--brand enter" style={{ ['--i' as string]: 1, textDecoration: 'none' }}>
                <span className="hero__label">{t('clientsGoodPayer')}</span>
                <b style={{ display: 'block', font: '400 30px/1.1 var(--font-serif)', marginTop: 8 }}>{displayName(best.client)}</b>
                <span className="small" style={{ color: 'var(--on-surface-strong-muted)' }}>
                  {t('clientsPaysIn')} {t('clientsPaysInDays', { days: best.stats.averageDaysToPay ?? 0 })}
                </span>
              </Link>
            ) : (
              <div className="kpi enter" style={{ ['--i' as string]: 1 }}>
                <span className="caption">{t('clientsInvoices')}</span>
                <b className="amount-lg">{invoices.filter((i) => i.clientId).length}</b>
                <span className="caption">{t('webBilledToSavedClients')}</span>
              </div>
            )}
            <div className="kpi enter" style={{ ['--i' as string]: 2 }}>
              <span className="caption">{t('webNewThisMonth')}</span>
              <b className="amount-lg">{newThisMonth}</b>
              <span className="caption">{t('webClientsAddedThisMonth', { count: newThisMonth })}</span>
            </div>
          </section>
          {visible.length === 0 ? (
            <div className="card">
              <EmptyState badge="search" title={t('clientsNoClientsMatch', { query })} />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('clientsClient')}</th>
                    <th>{t('clientsContact')}</th>
                    <th className="end">{t('clientsInvoices')}</th>
                    <th className="end">{t('clientsOwesYou')}</th>
                    <th className="end">{t('clientsPaidInTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(({ client, stats }) => (
                    <tr
                      key={client.id}
                      data-href={`/clients/${client.id}`}
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('a') || window.getSelection()?.toString()) return;
                        navigate(`/clients/${client.id}`);
                      }}
                    >
                      <td>
                        <span className="row" style={{ gap: 12 }}>
                          <Avatar initials={clientInitials(client)} seed={displayName(client)} />
                          <span className="stack-1">
                            <Link to={`/clients/${client.id}`} className="row-link">
                              {displayName(client)}
                            </Link>
                            {client.company.trim() && client.name.trim() && <span className="caption">{client.name}</span>}
                          </span>
                        </span>
                      </td>
                      <td className="muted">{contactLine(client)}</td>
                      <td className="end num">{stats.invoiceCount}</td>
                      <td className={`end amount${stats.overdue.isPositive ? ' danger-text' : ''}`}>
                        {stats.owed.format()}
                        {stats.overdue.isPositive && <span className="caption" style={{ display: 'block', color: 'var(--overdue)' }}>{t('webOverdueAmount', { amount: stats.overdue.format() })}</span>}
                      </td>
                      <td className="end amount">{stats.paid.format()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
